import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiKey, ApiKeysState } from "../../assets/interfaces/interfaces";

const initialState: ApiKeysState = {
  apiKeys: [],
  isLoading: false,
};

export const createAPIKey = createAsyncThunk(
  "apiKeys/createAPIKey",
  async (arg: { name: string; description: string }, { rejectWithValue }) => {
    try {
      const payload = {
        name: arg.name,
        ...(arg.description && { description: arg.description }),
      };

      const response = await fetch(
        `${process.env.REACT_APP_PYTHON_APi_URL}/api-keys`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        console.error("API Key creation error:", {
          status: response.status,
          statusText: response.statusText,
          data: errorData,
        });
        return rejectWithValue(errorData || response.statusText);
      }

      const data = await response.json();
      console.log("API key created successfully:", data);
      return data;
    }
    catch (error) {
      console.error("API Key creation error:", error);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);
export const fetchApiKeys = createAsyncThunk("apiKeys/fetchApiKeys",
  async (args, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PYTHON_APi_URL}/api-keys`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        return rejectWithValue(errorData || response.statusText);
      }

      const data = await response.json();
      return data;
    }
    catch (err) {
      if (err instanceof Error) {
        return rejectWithValue(err.message);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const deleteApiKey = createAsyncThunk("apiKeys/deleteApiKey",
  async (apiKeyId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PYTHON_APi_URL}/api-keys/${apiKeyId}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        return rejectWithValue(errorData || response.statusText);
      }

      const data = await response.json();
      console.log("API key deleted successfully:", data);
      return data.id;
    }
    catch (err) {
      if (err instanceof Error) {
        return rejectWithValue(err.message);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

const apiKeysSlice = createSlice({
  name: "apiKeys",
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAPIKey.fulfilled, (state, action: PayloadAction<ApiKey>) => {
        state.apiKeys.push(action.payload);
        state.isLoading = false;
      })
      .addCase(createAPIKey.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createAPIKey.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchApiKeys.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchApiKeys.fulfilled, (state, action: PayloadAction<ApiKey[]>) => {
        state.apiKeys = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchApiKeys.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteApiKey.fulfilled, (state, action) => {
        state.apiKeys = state.apiKeys.filter(key => key.id !== action.payload);
        state.isLoading = false;
      })
      .addCase(deleteApiKey.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteApiKey.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export default apiKeysSlice.reducer;
